import React from "react";
import {
	Control,
	Controller,
	UseFormWatch,
	UseFormSetValue,
	FieldPath,
} from "react-hook-form";
import {
	TextField,
	Grid,
	Button,
	Box,
	IconButton,
	Checkbox,
	FormControlLabel,
	Typography,
} from "@mui/material";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { NeutralResetRule } from "../../../domain/reward.interface";
import { RewardsConfigFormData } from "./RewardsConfigurationModal";

interface CustomNeutralResetSectionProps {
	control: Control<RewardsConfigFormData>;
	watch: UseFormWatch<RewardsConfigFormData>;
	setValue: UseFormSetValue<RewardsConfigFormData>;
	isViewMode: boolean;
}

const CustomNeutralResetSection: React.FC<CustomNeutralResetSectionProps> = ({
	control,
	watch,
	setValue,
	isViewMode,
}) => {
	const customNeutralResetEnabled = watch("customNeutralReset.enabled");
	const customNeutralResetRules = watch("customNeutralReset.rules") || [];

	const handleAddNeutralResetRule = () => {
		const currentRules = customNeutralResetRules;
		const newRule: NeutralResetRule = {
			resultId: "",
			consecutiveCount: 3,
		};
		setValue("customNeutralReset.rules", [...currentRules, newRule]);
	};

	const handleRemoveNeutralResetRule = (index: number) => {
		const currentRules = customNeutralResetRules;
		setValue(
			"customNeutralReset.rules",
			currentRules.filter((_, i) => i !== index),
		);
	};

	return (
		<>
			{/* Enable Custom Neutral Reset Checkbox */}
			<Grid item xs={12}>
				<Controller
					name="customNeutralReset.enabled"
					control={control}
					render={({ field }) => (
						<FormControlLabel
							control={
								<Checkbox
									{...field}
									checked={field.value || false}
									disabled={isViewMode}
								/>
							}
							label="Enable Custom Neutral Reset (Repeating Neutrals Detection)"
						/>
					)}
				/>
			</Grid>

			{customNeutralResetEnabled && (
				<>
					<Grid item xs={12}>
						<Typography variant="h6" gutterBottom>
							Custom Neutral Reset Configuration
						</Typography>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							Define rules to detect repeating neutral results that should
							trigger resets. When a neutral result repeats consecutively the
							specified number of times, it will be treated as invalid and
							trigger reset logic.
						</Typography>
					</Grid>

					{/* Description */}
					<Grid item xs={12}>
						<Controller
							name="customNeutralReset.description"
							control={control}
							render={({ field }) => (
								<TextField
									{...field}
									label="Description (optional)"
									fullWidth
									variant="outlined"
									multiline
									rows={2}
									disabled={isViewMode}
									placeholder="e.g., Reset ladder when Dilute results appear 3 times in a row"
								/>
							)}
						/>
					</Grid>

					{/* Neutral Reset Rules */}
					<Grid item xs={12}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mb: 1,
							}}
						>
							<Typography variant="subtitle1">Neutral Reset Rules</Typography>
							{!isViewMode && (
								<Button
									size="small"
									startIcon={<AddCircleOutlineIcon />}
									onClick={handleAddNeutralResetRule}
									variant="outlined"
								>
									Add Rule
								</Button>
							)}
						</Box>
					</Grid>

					{customNeutralResetRules.map((rule, index) => (
						<Grid item xs={12} key={index}>
							<Box
								sx={{
									p: 2,
									border: "1px solid #e0e0e0",
									borderRadius: 1,
									position: "relative",
								}}
							>
								{!isViewMode && (
									<IconButton
										size="small"
										onClick={() => handleRemoveNeutralResetRule(index)}
										sx={{ position: "absolute", top: 8, right: 8 }}
									>
										<RemoveCircleIcon />
									</IconButton>
								)}
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Typography variant="subtitle2" gutterBottom>
											Rule {index + 1}
										</Typography>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name={
												`customNeutralReset.rules.${index}.resultId` as FieldPath<RewardsConfigFormData>
											}
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Result ID"
													fullWidth
													variant="outlined"
													disabled={isViewMode}
													placeholder="e.g., Dilute, Missing, Refused"
													helperText="The neutral result ID to track"
												/>
											)}
										/>
									</Grid>
									<Grid item xs={6}>
										<Controller
											name={
												`customNeutralReset.rules.${index}.consecutiveCount` as FieldPath<RewardsConfigFormData>
											}
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Consecutive Count"
													fullWidth
													variant="outlined"
													type="number"
													disabled={isViewMode}
													helperText="How many times in a row to trigger reset"
													inputProps={{ min: 1 }}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					))}

					{customNeutralResetRules.length === 0 && (
						<Grid item xs={12}>
							<Typography
								variant="body2"
								color="textSecondary"
								sx={{ fontStyle: "italic" }}
							>
								No neutral reset rules defined. Click "Add Rule" to create one.
							</Typography>
						</Grid>
					)}
				</>
			)}
		</>
	);
};

export default CustomNeutralResetSection;
